/**
 * ContingencyContractCard — one Wisconsin contingency, as the signed offer
 * prints it (design §5.3): contract status, deadline in Central time, cure
 * terms, attention flags, the actions the server allows this user, the
 * inspection/appraisal/financing tools (not notices) and the timeline.
 */

import React, { useState } from 'react';
import { NOTICE_TYPES } from '../../../agents/contingencyNoticesAgent';
import { contractCopy, hasPlaceholder } from './contractCopy';
import {
  CONTRACT_STATUS,
  TERMINAL_STATUSES,
  attentionFlags,
  cureDeadlineDate,
  cureRightLabel,
  deadlineBasisLabel,
  deadlineDate,
  deadlinePassedNote,
  statusLabel,
  statusTone,
} from './contractFormat';
import ContingencyTimeline from './ContingencyTimeline';
import FlagActions from './FlagActions';
import SellerCurePanel, { showsSellerCurePanel } from './SellerCurePanel';
import InspectionFlow from '../InspectionFlow';
import AppraisalFlow from '../AppraisalFlow';
import FinancingFlow from '../FinancingFlow';
import styles from './ContingencyContractCard.module.css';

const TOOL_FLOWS = { inspection: InspectionFlow, appraisal: AppraisalFlow, financing: FinancingFlow };
const PROPOSAL_TYPES = ['inspection', 'appraisal'];

function Tools({ contingency, onRefresh }) {
  const [open, setOpen] = useState(false);
  const Flow = TOOL_FLOWS[contingency.contingency_type];
  if (!Flow) return null;
  const panelId = `tools-${contingency.id}`;
  return (
    <section className={styles.tools} aria-labelledby={`${panelId}-heading`}>
      <div className={styles.toolsHead}>
        <h4 id={`${panelId}-heading`} className={styles.subheading}>{contractCopy('WI.hub.tools.heading')}</h4>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          data-testid={`tools-toggle-${contingency.id}`}
        >
          {open ? 'Hide tools' : 'Show tools'}
        </button>
      </div>
      {open && (
        <div id={panelId} className={styles.toolsBody}>
          {PROPOSAL_TYPES.includes(contingency.contingency_type) && (
            <div className={styles.proposals} role="note" data-testid={`proposals-banner-${contingency.id}`}>
              <p className={styles.proposalsHeading}>{contractCopy('WI.hub.proposals.heading')}</p>
              <p>{contractCopy('WI.hub.proposals.banner')}</p>
            </div>
          )}
          <Flow contingency={contingency} onRefresh={onRefresh} />
        </div>
      )}
    </section>
  );
}

function Fact({ label, children }) {
  if (!children) return null;
  return (
    <div className={styles.fact}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function ContingencyContractCard({
  contingency,
  role,
  events = [],
  eventsLoading = false,
  eventsError = null,
  onRetryEvents,
  noticesAvailable = false,
  dealEnded = false,
  windDownPending = false,
  onDeliver,
  onChanged,
  onRefresh,
  focused = false,
}) {
  const rawContract = contingency.contract || {};
  // Ending a deal moves every other row to `moot` (T16) in the same commit,
  // so rows arrive closed. The one exception is a deal the Phase 3 stub ended
  // before Phase 5 shipped: its rows stay open until the wind-down finishes
  // (`terminated.wind_down_pending`); never show "notice due" there.
  const contract = dealEnded && windDownPending && !TERMINAL_STATUSES.includes(rawContract.status)
    ? { ...rawContract, status: CONTRACT_STATUS.MOOT, deadline_passed: false }
    : rawContract;
  const status = contract.status;
  const tone = statusTone(status);
  const heading = contract.heading || 'Contingency';
  const allowed = contract.allowed_actions || [];
  const isOther = contract.catalog_key === 'other';
  const actionsLive = noticesAvailable && !dealEnded;
  const myEvents = events.filter((e) => e.contingency_id === contingency.id);
  const flags = attentionFlags(contract, myEvents);
  const passedNote = deadlinePassedNote(contract);
  const canBuyerNotice = role === 'buyer'
    && (allowed.includes(NOTICE_TYPES.NOT_SATISFIED) || allowed.includes(NOTICE_TYPES.WAIVER));
  const canRecordApproval = allowed.includes(NOTICE_TYPES.LAND_DIVISION_APPROVED);
  const canEndLand = allowed.includes(NOTICE_TYPES.LAND_DIVISION_END);
  const lapsedText = status === CONTRACT_STATUS.LAND_DIVISION_LAPSED
    ? contractCopy('WI.land_division.lapsed_panel', { deadline_date: deadlineDate(contract) })
    : null;
  const hasActions = canBuyerNotice || showsSellerCurePanel(contract, role)
    || canRecordApproval || canEndLand || Boolean(lapsedText);
  const headingId = `contract-card-${contingency.id}`;
  // `focused`: the card a notification opened the hub on; ContractHub focuses it.
  const deadline = deadlineDate(contract);
  const legacyEstimate = contract.deadline_basis === 'legacy_platform_accept';

  return (
    <article
      className={`${styles.card} ${styles[`tone_${tone}`] || ''} ${focused ? styles.focused : ''}`}
      aria-labelledby={headingId}
      tabIndex={focused ? -1 : undefined}
      data-focused={focused || undefined}
      data-testid={`contract-card-${contingency.id}`}
      data-status={status}
    >
      <header className={styles.header}>
        <h3 id={headingId} className={styles.heading}>{heading}</h3>
        {!isOther && (
          <span className={`${styles.pill} ${styles[`pill_${tone}`] || ''}`} data-testid={`contract-status-${contingency.id}`}>
            {statusLabel(contract, role)}
          </span>
        )}
      </header>

      {isOther ? (
        <div className={styles.other}>
          <p className={styles.muted}>{contractCopy('WI.hub.other_terms.help')}</p>
          {contract.other_terms && <blockquote className={styles.otherTerms}>{contract.other_terms}</blockquote>}
        </div>
      ) : (
        <>
          {passedNote && <p className={styles.passed} data-testid={`deadline-passed-${contingency.id}`}>{passedNote}</p>}
          {/* Copy §15.1: only a lead-based paint row can be recorded as not applying. */}
          {status === CONTRACT_STATUS.DOES_NOT_APPLY && (
            <p className={styles.doesNotApply} role="note" data-testid={`does-not-apply-${contingency.id}`}>
              {contractCopy('WI.hub.does_not_apply.lead_paint')}
            </p>
          )}

          <dl className={styles.facts}>
            <Fact label="Deadline">
              {/* An open card's pill already states the deadline. */}
              {deadline && (status !== CONTRACT_STATUS.OPEN || legacyEstimate) && (
                <>
                  {`11:59 p.m. Central time, ${deadline}`}
                  {legacyEstimate && (
                    <span className={styles.estimate}>{contractCopy('WI.transaction.contingency_deadline_legacy.label')}</span>
                  )}
                </>
              )}
            </Fact>
            <Fact label="How the deadline was set">{deadlineBasisLabel(contract.deadline_basis)}</Fact>
            <Fact label="Seller's right to cure">{cureRightLabel(contract.cure)}</Fact>
            <Fact label="Cure deadline">
              {cureDeadlineDate(contract) ? `11:59 p.m. Central time, ${cureDeadlineDate(contract)}` : null}
            </Fact>
          </dl>

          {contract.catalog_key === 'title' && (
            <p className={styles.muted}>{contractCopy('WI.hub.title_objection.note')}</p>
          )}
        </>
      )}

      {flags.length > 0 && (
        <ul className={styles.flags} aria-label="Flags for review" data-testid={`attention-flags-${contingency.id}`}>
          {flags.map((flag) => (
            <li key={flag.reason} className={styles.flag} data-reason={flag.reason}>
              {flag.short && <strong className={styles.flagShort}>{flag.short}</strong>}
              {flag.message && <span className={styles.flagMessage}>{flag.message}</span>}
            </li>
          ))}
        </ul>
      )}

      {actionsLive && !isOther && hasActions && (
        <div className={styles.actions}>
          {canBuyerNotice && (
            <button
              type="button"
              className={styles.primary}
              onClick={() => onDeliver(contingency, null)}
              data-testid={`deliver-notice-${contingency.id}`}
            >
              Deliver written notice
            </button>
          )}

          {showsSellerCurePanel(contract, role) && (
            <SellerCurePanel
              contingencyId={contingency.id}
              contract={contract}
              noticesAvailable={noticesAvailable}
              onDeliver={(type) => onDeliver(contingency, type)}
            />
          )}

          {(canRecordApproval || canEndLand || lapsedText) && (
            <div className={styles.land} data-testid={`land-division-${contingency.id}`}>
              {lapsedText && !hasPlaceholder(lapsedText) && <p>{lapsedText}</p>}
              <div className={styles.buttonRow}>
                {canRecordApproval && (
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => onDeliver(contingency, NOTICE_TYPES.LAND_DIVISION_APPROVED)}
                    data-testid={`land-record-${contingency.id}`}
                  >
                    {contractCopy('WI.land_division.action.record')}
                  </button>
                )}
                {canEndLand && (
                  <button
                    type="button"
                    className={styles.danger}
                    onClick={() => onDeliver(contingency, NOTICE_TYPES.LAND_DIVISION_END)}
                    data-testid={`land-end-${contingency.id}`}
                  >
                    {contractCopy('WI.land_division.action.end')}
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Flags follow the server's flag_actions, which can outlive the deal
          (e.g. recording a notice delivered another way). */}
      {noticesAvailable && !isOther && (contract.flag_actions || []).length > 0 && (
        <div className={styles.flagRow}>
          <FlagActions contingency={contingency} onChanged={onChanged} />
        </div>
      )}

      {role === 'buyer' && !dealEnded && !isOther && <Tools contingency={contingency} onRefresh={onRefresh} />}

      {!isOther && (
        <ContingencyTimeline
          contingencyId={contingency.id}
          contract={contract}
          events={events}
          loading={eventsLoading}
          error={eventsError}
          onRetry={onRetryEvents}
          role={role}
        />
      )}
    </article>
  );
}
